<template>
  <div class="product__fund">
    <h2
      v-if="!isProductPage"
      class="mb-0 mt-md-0 mt-4 cursor-pointer"
      @click="handleProductClick"
    >
      {{ product.name }}
    </h2>
    <div v-if="productDescription" class="product__fund-text mt-2 mb-5">
      <ViewMore :text="productDescription" :lines="4" :line-height="22" />
    </div>

    <FundingProgress :funding="funding" :currency="currency" />

    <DonationOptionsInfo v-if="isProductPage && donationOptions.length" />

    <div v-if="isProductPage && !donationOptions.length" class="mt-4">
      <span class="donation-options__no-options-text"
        ><b>No available options for your region</b></span
      >
    </div>

    <div v-if="isProductPage">
      <DonationOptions
        v-for="option in donationOptions"
        :key="option.id"
        :title="option.title"
        :description="option.description"
        :backers="option.backers"
        :type="option.type"
        :availability-date="product.expectedReleaseDate"
      >
        <template #price-info>
          <div
            v-if="option.type === 'noReward'"
            class="col price-selector mr-3 p-2 d-flex align-items-center justify-content-between"
          >
            <div class="formatted-price">
              {{ formatMoney(option.price.amount, currency) }}
            </div>
            <div class="arrows d-flex flex-column">
              <img
                class="cursor-pointer"
                src="@/assets/icons/arrows/funding-up.svg"
                @click="adjustAmount(1)"
              />
              <img
                class="cursor-pointer"
                src="@/assets/icons/arrows/funding-down.svg"
                @click="adjustAmount(-1)"
              />
            </div>
          </div>

          <div
            v-if="option.type !== 'noReward'"
            class="price-info d-flex align-items-end"
          >
            <div class="d-flex align-items-end">
              <div>
                {{ formatMoney(option.price.amount, currency) }}
              </div>
              <span
                v-if="option.price.taxBehaviour === 'exclusive'"
                class="price-info__tax"
              >
                &nbsp;+&nbsp;{{ $t('tax') }}
              </span>
            </div>
          </div>
        </template>

        <template #button>
          <div class="col col-lg-8 col-md-4 col-5 p-0">
            <Button
              full-width
              :variant="option.buttonVariant"
              @click="
                handleProceedDonation({
                  reward: option.type,
                  donationOptionId: option.id,
                  donationOptionPriceId: option.price._id
                })
              "
            >
              {{ option.buttonText }}
            </Button>
          </div>
        </template>
      </DonationOptions>
    </div>

    <TermsInfo v-if="isProductPage" :closing-date="funding.closingDate" />

    <FAQ v-if="isProductPage" />

    <Button
      v-if="!isProductPage"
      class="w-100 mt-5"
      full-width
      @click="handleViewFundingOptions(item)"
    >
      View funding options
    </Button>
  </div>
</template>

<script>
import { find } from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import FundingProgress from './FundingProgress';
import DonationOptionsInfo from './DonationOptionsInfo';
import DonationOptions from './DonationOptions';
import TermsInfo from './TermsInfo';
import FAQ from './FAQ';
import { CarouselModes } from '@/misc/constants';
import formatMoney from '@/misc/formatMoney';
import getDonationOptionTitle from '@/misc/getDonationOptionTitle';
import getCurrencyByRegion from '@/misc/getCurrencyByRegion';
import { createCheckoutSession } from '@/services/checkout';

import Button from '@/components/Elements/Button.vue';
import ViewMore from '@/components/ViewMore';

export default {
  name: 'FundProduct',

  components: {
    ViewMore,
    Button,
    FundingProgress,
    DonationOptionsInfo,
    TermsInfo,
    DonationOptions,
    FAQ
  },

  props: {
    product: {
      type: Object,
      default: () => {}
    },
    funding: {
      type: Object,
      default: () => {}
    },
    isProductPage: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      CarouselModes,
      currentNoRewardPriceIndex: 0 // as default
    };
  },

  computed: {
    ...mapGetters({
      redirectUrl: 'checkout/redirectUrl',
      region: 'config/region'
    }),
    ...mapState('config', ['config']),
    currency() {
      return getCurrencyByRegion(this.region);
    },
    productDescription() {
      return this.product?.fullDescription || this.product?.shortDescription;
    },
    mainNoRewardOption() {
      const optionsFilteredByPrice = (
        this.funding?.donationOptions || []
      ).filter(({ prices }) =>
        prices.some((price) => price.region === this.region)
      );

      const noRewardOptions = optionsFilteredByPrice.filter(
        (opt) => !opt.reward
      );

      const mainNoRewardOption = this.$get(noRewardOptions, '[0]', null);

      return mainNoRewardOption;
    },
    noRewardPrices() {
      return this.mainNoRewardOption.prices.filter(
        (price) => price.region === this.region
      );
    },
    donationOptions() {
      const optionsFilteredByPrice = (
        this.funding?.donationOptions || []
      ).filter(({ prices }) =>
        prices.some((price) => price.region === this.region)
      );

      const optionsWithReward = optionsFilteredByPrice.filter(
        (opt) => opt.reward
      );

      return [
        ...(this.mainNoRewardOption
          ? [
              {
                id: this.mainNoRewardOption._id,
                title: getDonationOptionTitle({
                  title: this.mainNoRewardOption?.title,
                  prices: this.noRewardPrices,
                  currency: this.currency
                }),
                description: this.mainNoRewardOption?.description,
                type: 'noReward',
                backers:
                  this.funding.backersByFormat.find(
                    (backerInfo) => !backerInfo.reward
                  )?.backerCount || 0,
                price: this.currentDonationOptionNoRewardPrice,
                buttonText: 'Donate',
                buttonVariant: 'primary'
              }
            ]
          : []),
        ...optionsWithReward.map((opt) => ({
          id: opt._id,
          title: opt.title,
          description: opt.description,
          type: opt.reward,
          backers:
            find(this.funding.backersByFormat, {
              reward: opt.reward,
              formatId: opt.formatId
            })?.backerCount || 0,
          price: opt.prices.find((price) => price.region === this.region),
          buttonText: 'Donate',
          buttonVariant: 'primary'
        }))
      ];
    },

    currentDonationOptionNoRewardPrice() {
      const selectedOption =
        this.noRewardPrices[this.currentNoRewardPriceIndex] || null;

      return (
        selectedOption || {
          amount: 0,
          region: this.region
        }
      );
    }
  },

  methods: {
    formatMoney,
    getCurrencyByRegion,
    ...mapActions({
      createSession: 'checkout/createSession'
    }),
    ...mapMutations({
      ADD_ITEM: 'checkout/ADD_ITEM'
    }),
    handleProductClick() {
      this.$router.push({
        path: `/products/${this.$get(this.product, '_id')}`
      });
    },
    adjustAmount(delta) {
      let newIndex = this.currentNoRewardPriceIndex + delta;

      if (newIndex < 0) {
        newIndex = this.noRewardPrices.length - 1;
      } else if (newIndex >= this.noRewardPrices.length) {
        newIndex = 0;
      }

      this.currentNoRewardPriceIndex = newIndex;
    },

    handleProceedDonation({ reward, donationOptionId, donationOptionPriceId }) {
      let donationOption;

      if (reward === 'noReward') {
        donationOption = this.mainNoRewardOption;
      } else {
        donationOption = this.funding.donationOptions.find(
          (opt) => opt._id === donationOptionId
        );
      }

      const payload = {
        items: [
          {
            productId: this.product._id,
            donationOptionId: donationOption._id,
            donationOptionPriceId,
            previewUrl: this.product.previewImages[0]?.url
          }
        ],
        storeRegion: this.region,
        isoCurrency: this.currency,
        customerUserAgent: window.navigator.userAgent,
        successUrl: `${window.location.origin}/funding/success/?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href,
        sessionType: 'donation'
      };

      createCheckoutSession(this.$axios, payload)
        .then((response) => {
          window.location.href = response.redirectUrl;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            'danger'
          );
        })
        .finally(() => {
          this.loadingToCheckout = false;
        });
    },
    handleViewFundingOptions() {
      this.$router.push(`/funding/cookbooks/${this.product._id}`);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../styles/utilites';
@import '../../styles/components';

.product {
  overflow-x: hidden;

  &__fund {
    // hide scrollbar
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }

    &-text {
      line-height: 22px;
    }
  }
}

.donation-form {
  &__label {
    color: $dark-color;
    font-size: 12px;
    line-height: 20px;
  }

  &__counter {
    font-size: 12px;
    color: $grey-color;
  }

  &__input {
    min-height: 48px;
    border-radius: 8px;
    border-color: $light-grey-color;
  }
}

.donation-options {
  &__no-options-text {
    font-size: 18px;
  }
}

.donation-opion__button-disabled {
  border: 1px solid black;
  padding: 15px 30px;
  border-radius: 10px;
  cursor: not-allowed;
}

.donation-opion__button-disabled:disabled {
  opacity: 1;
}
</style>
